// SPDX-License-Identifier: AGPL-3.0-or-later

import type React from 'react';
import {useCallback, useEffect, useRef, useState} from 'react';

export interface UseScrollSpyOptions {
	sectionIds: ReadonlyArray<string>;
	containerRef: React.RefObject<HTMLElement | null>;
	offset?: number;
}

export interface UseScrollSpyReturn {
	activeSectionId: string | null;
	scrollToSection: (sectionId: string) => void;
}

interface SectionPosition {
	id: string;
	top: number;
}

function getSectionElement(container: HTMLElement, id: string): HTMLElement | null {
	const element = document.getElementById(id);
	if (!(element instanceof HTMLElement) || !container.contains(element)) {
		return null;
	}
	return element;
}

export function useScrollSpy({sectionIds, containerRef, offset = 68}: UseScrollSpyOptions): UseScrollSpyReturn {
	const [activeSectionId, setActiveSectionId] = useState<string | null>(sectionIds[0] ?? null);
	const rafIdRef = useRef<number | null>(null);
	const sectionPositionsRef = useRef<Array<SectionPosition>>([]);
	const maxScrollTopRef = useRef(0);
	useEffect(() => {
		const container = containerRef.current;
		if (!container || sectionIds.length === 0) {
			sectionPositionsRef.current = [];
			maxScrollTopRef.current = 0;
			setActiveSectionId(null);
			return;
		}
		let shouldMeasure = false;
		let resizeObserver: ResizeObserver | null = null;
		const observedElements = new Set<Element>();
		const updateActiveFromCache = () => {
			const scrollTop = container.scrollTop;
			const isAtBottom = scrollTop >= maxScrollTopRef.current - 1;
			const target = scrollTop + offset + 1;
			let nextActive: string | null = null;
			for (const position of sectionPositionsRef.current) {
				if (position.top <= target) {
					nextActive = position.id;
				} else if (nextActive) {
					break;
				}
			}
			if (!nextActive && sectionIds.length > 0) {
				nextActive = sectionIds[0] ?? null;
			}
			if (isAtBottom && sectionIds.length > 0) {
				nextActive = sectionIds[sectionIds.length - 1] ?? nextActive;
			}
			setActiveSectionId((prev) => (prev === nextActive ? prev : nextActive));
		};
		const observeElement = (element: Element) => {
			if (!resizeObserver || observedElements.has(element)) {
				return;
			}
			resizeObserver.observe(element);
			observedElements.add(element);
		};
		const pruneObservedElements = () => {
			if (!resizeObserver) {
				return;
			}
			for (const element of observedElements) {
				if (element === container) {
					continue;
				}
				if (!container.contains(element)) {
					resizeObserver.unobserve(element);
					observedElements.delete(element);
				}
			}
		};
		const measureSections = () => {
			const containerRect = container.getBoundingClientRect();
			const scrollTop = container.scrollTop;
			const nextPositions: Array<SectionPosition> = [];
			for (const id of sectionIds) {
				const element = getSectionElement(container, id);
				if (!element) {
					continue;
				}
				observeElement(element);
				const rect = element.getBoundingClientRect();
				nextPositions.push({
					id,
					top: rect.top - containerRect.top + scrollTop,
				});
			}
			sectionPositionsRef.current = nextPositions;
			maxScrollTopRef.current = Math.max(0, container.scrollHeight - container.clientHeight);
			pruneObservedElements();
			updateActiveFromCache();
		};
		const scheduleUpdate = (needsMeasure: boolean) => {
			shouldMeasure = shouldMeasure || needsMeasure;
			if (rafIdRef.current != null) return;
			rafIdRef.current = window.requestAnimationFrame(() => {
				rafIdRef.current = null;
				if (shouldMeasure) {
					shouldMeasure = false;
					measureSections();
					return;
				}
				updateActiveFromCache();
			});
		};
		if (typeof ResizeObserver !== 'undefined') {
			resizeObserver = new ResizeObserver(() => scheduleUpdate(true));
			observeElement(container);
		}
		scheduleUpdate(true);
		const handleScroll = () => scheduleUpdate(false);
		const handleResize = () => scheduleUpdate(true);
		container.addEventListener('scroll', handleScroll, {passive: true});
		window.addEventListener('resize', handleResize);
		return () => {
			container.removeEventListener('scroll', handleScroll);
			window.removeEventListener('resize', handleResize);
			resizeObserver?.disconnect();
			if (rafIdRef.current != null) {
				cancelAnimationFrame(rafIdRef.current);
				rafIdRef.current = null;
			}
		};
	}, [sectionIds, containerRef, offset]);
	const scrollToSection = useCallback(
		(sectionId: string) => {
			const container = containerRef.current;
			if (!container) {
				return;
			}
			const element = getSectionElement(container, sectionId);
			if (!element) {
				return;
			}
			const containerRect = container.getBoundingClientRect();
			const elementRect = element.getBoundingClientRect();
			const scrollTop = container.scrollTop + (elementRect.top - containerRect.top) - offset;
			container.scrollTo({
				top: scrollTop,
				behavior: 'auto',
			});
			setActiveSectionId(sectionId);
		},
		[containerRef, offset],
	);
	return {activeSectionId, scrollToSection};
}
